// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Public entry point for the SVG phylogenetic tree viewer.
//
// TheiaPhyloViewer ties the core graph-building pipeline to the SVG render
// modules via a thin class with a props-centric API.  Each call to
// setProps()/render() rebuilds the graph and re-mounts the SVG layers;
// the view (pan/zoom state) is preserved across renders.
//
// M4: interaction layer — hover highlight, leaf selection, native tooltip,
// scalebar, and the public API methods selectNode / selectLeafNodes /
// highlightNode / getHighlightedNode.
// M5: export — exportSVG (standalone document) and exportPNG (canvas raster).

import {
  buildGraph,
  rerootOnEdge,
  findMidpoint,
  ladderise,
  serialiseNewick,
  buildSession,
  createAnnotationEntry,
  updateAnnotationEntry,
  deleteAnnotationEntry,
  isOrthogonal as isOrthogonalType,
  getPixelOffsets as computePixelOffsets,
  getAlignLeafLabels as computeAlignLeafLabels,
  hasLeafLabels as computeHasLeafLabels,
  hasMetadata as computeHasMetadata,
  hasMetadataLabels as computeHasMetadataLabels,
  hasMetadataHeaders as computeHasMetadataHeaders,
  getMetadataColumnWidth as computeMetadataColumnWidth,
  TreeTypes,
} from "./core/index.js";
import { defaultProps } from "./core/defaults.js";
import {
  zoomForScale,
  scaleForZoom,
  worldToClient,
  clientToWorld,
} from "./render/camera.js";
import { renderEdges } from "./render/edges.js";
import { renderNodeShapes } from "./render/shapes.js";
import { renderLeafLabels } from "./render/labels.js";
import { renderMetadataBlocks, renderMetadataHeaders } from "./render/metadata.js";
import { renderInternalLabels } from "./render/internal-labels.js";
import { renderBranchLabels } from "./render/branch-labels.js";
import { createView } from "./render/view.js";
import { renderPiecharts } from "./render/piecharts.js";
import { renderCladeAnnotationShapes, renderCladeAnnotationLabels } from "./render/clade-annotations.js";
import { renderLeafLabelAnnotations } from "./render/leaf-label-annotations.js";
import { renderHalos } from "./render/halos.js";
import { renderScalebar } from "./render/scalebar.js";
import { renderLogo } from "./render/logo.js";
import { rgba, tooltipTitle } from "./render/dom.js";

// Supported maximum leaf count (project target). Beyond this, SVG DOM node
// counts make rendering sluggish; density thinning still caps label count, but
// edges/shapes scale with leaves. We warn once per instance rather than block.
const PERF_MAX_LEAVES = 1000;

/**
 * Determine whether a specific interaction capability is enabled.
 *
 *   interactive === true          -> all capabilities on
 *   interactive[capability]===true -> that capability on
 *   otherwise                      -> off
 *
 * Note: pan/zoom stay always-on (an M1 decision).
 * Only highlight and tooltip are gated here. Click→select always fires
 * (the capability set is pan / zoom / highlight / tooltip).
 *
 * @param {object} props
 * @param {'highlight'|'tooltip'} capability
 * @returns {boolean}
 */
function hasInteraction(props, capability) {
  return (
    (props.interactive ?? false) === true ||
    (props.interactive != null &&
      typeof props.interactive === "object" &&
      props.interactive[capability] === true)
  );
}

export class TheiaPhyloViewer {
  /**
   * @param {HTMLElement} container - Host element; the viewer fills it.
   * @param {Object} props - Initial props, merged over defaultProps.
   *   `source` (string|object) — Newick string or pre-parsed tree.
   *   `size`   ({ width, height }) — defaults to container dimensions or 800x600.
   *   All other props from defaultProps are accepted.
   */
  constructor(container, props = {}) {
    this.container = container;
    this.props = { ...defaultProps, ...props };

    if (!this.props.size) {
      this.props.size = {
        width: container.clientWidth || 800,
        height: container.clientHeight || 600,
      };
    }

    this._size = { width: this.props.size.width, height: this.props.size.height };

    // Internal highlight state (not a prop — swapped without triggering full render).
    this._highlightedId = this.props.highlightedId ?? null;

    // Track last scale to detect zoom (vs pure pan) in _onViewChange.
    this._lastScale = null;

    // Pending requestAnimationFrame handle for coalesced label re-render on zoom.
    this._labelRaf = null;
    // Whether the one-time large-tree perf warning has fired (see render()).
    this._warnedLargeTree = false;
    // Bumped by re-hang ops (setRoot/rerootNode/midpointRoot) to force a refit
    // on the next render; collapse/expand deliberately do not bump it.
    this._topoVersion = 0;
    // When suspended, render() short-circuits so a batch of setProps() calls can
    // be applied without intermediate repaints; resume() does the single redraw.
    this._suspended = false;

    this._view = createView(container, this.props.size, {
      onNodeHover: (id, col) => this._onHover(id, col),
      onNodeClick: (id, opts) => this._onClick(id, opts),
      onViewChange: ({ scale, viewBox }) => this._onViewChange(scale, viewBox),
    });

    this.render();
  }

  // ---- Interaction handlers -----------------------------------------------

  /**
   * Called by view.js when the hovered node changes.
   * Gated on interactive.highlight and interactive.tooltip.
   *
   * @param {string|null} id - Node id of the hit element (or null).
   * @param {string|null} col - Metadata column name when hovering a metadata block rect (or null).
   */
  _onHover(id, col) {
    const doHighlight = hasInteraction(this.props, "highlight");
    const doTooltip   = hasInteraction(this.props, "tooltip");

    if (!doHighlight && !doTooltip) return;

    if (doHighlight) {
      const prev = this._highlightedId;
      // Resolve the node to verify it exists and is not hidden.
      const node = id && this._graph ? this._graph.nodeById[id] : null;
      const nextId = (node && !node.isHidden) ? node.id : null;
      if (nextId !== prev) {
        this._highlightedId = nextId;
        this._view.replaceHalos(renderHalos(
          this._graph,
          { ...this.props, highlightedId: this._highlightedId },
          this._view.getScale(),
        ));
      }
    }

    if (doTooltip) {
      let text = "";
      const node = id && this._graph ? this._graph.nodeById[id] : null;
      if (node) {
        if (node.isLeaf) {
          text = node.label ?? node.id;
        } else {
          text = `Leaves in subtree: ${node.totalLeaves}.`;
          // node.label is set only on leaves; an internal node's Newick label
          // lives in node.name. Using node.name fulfils spec §2 (show the
          // subtree label for named internal nodes).
          if (node.name) {
            text += ` Subtree label: ${node.name}.`;
          }
        }
        // Append metadata column value when hovering a metadata block.
        const entry = this.props.metadata && this.props.metadata[id] && this.props.metadata[id][col];
        if (col && entry) {
          text += `. ${col}: ${entry.label != null ? entry.label : rgba(entry.colour)}.`;
        }
      }
      // tooltipTitle appends a random run of zero-width spaces so the browser
      // re-displays the native title even when the visible text is unchanged,
      // and clears to "" for falsy text.
      this.container.title = tooltipTitle(text, Math.floor(Math.random() * 16));
    }
  }

  /**
   * Called by view.js when a click (not drag) is detected.
   * Click→select always fires (no capability gate): empty space → clear all;
   * leaf → toggle (append) or replace selectedIds; internal node → no-op.
   * Pan/zoom stay always-on regardless.
   *
   * @param {string|null} id
   * @param {{ append: boolean }} opts
   */
  _onClick(id, { append } = {}) {
    const node = id && this._graph ? this._graph.nodeById[id] : null;

    // Authoring modes intercept the click before the select/collapse gestures.
    if (this.props.annotationMode) {
      if (node && !node.isLeaf) this.createAnnotation({ nodeId: node.id });
      return;
    }
    if (this.props.leafLabelAnnotationMode) {
      if (node) this.createLeafLabelAnnotation({ nodeId: node.id });
      return;
    }

    if (node && !node.isLeaf) {
      // Internal node → toggle collapse (the click-to-collapse gesture).
      // Collapsing rebuilds the graph, so route through setProps (a full render)
      // and return BEFORE the halo-only repaint below, which assumes the graph
      // is unchanged.
      const set = new Set(this.props.collapsedIds ?? []);
      if (set.has(node.id)) {
        set.delete(node.id);
      } else {
        set.add(node.id);
      }
      this.setProps({ collapsedIds: Array.from(set) });
      return;
    }

    if (node && node.isLeaf) {
      // Leaf: toggle membership (append=true) or replace (append=false).
      let next;
      if (append) {
        const current = new Set(this.props.selectedIds ?? []);
        if (current.has(node.id)) {
          current.delete(node.id);
        } else {
          current.add(node.id);
        }
        next = Array.from(current);
      } else {
        next = [node.id];
      }
      this.props = { ...this.props, selectedIds: next };
    } else if (!node) {
      // Click on empty space: clear selection.
      this.props = { ...this.props, selectedIds: [] };
    }

    this._view.replaceHalos(renderHalos(
      this._graph,
      { ...this.props, highlightedId: this._highlightedId },
      this._view.getScale(),
    ));
  }

  /**
   * Called by view.js on every viewBox mutation (pan or zoom).
   *
   * Scalebar: rerender on EVERY call (pan moves the viewBox corner).
   * Halos: rerender ONLY when scale changed (pure pan tracks halos
   * automatically because they live inside contentGroup).
   *
   * @param {number} scale
   * @param {{ x, y, w, h }} viewBox
   */
  _onViewChange(scale, viewBox) {
    if (this._graph) {
      // Scalebar and logo always rerender — both anchor to a viewBox corner, so
      // a pure pan (viewBox.x/y change) must reposition them.
      this._view.setScalebar(renderScalebar(this._graph, this.props, scale, viewBox));
      this._view.setLogo(renderLogo(this.props, viewBox, scale));

      // Halos and labels only rerender on scale change (counter-scale ratio changes).
      if (scale !== this._lastScale) {
        this._lastScale = scale;
        this._view.replaceHalos(renderHalos(
          this._graph,
          { ...this.props, highlightedId: this._highlightedId },
          scale,
        ));
        this._scheduleLabelUpdate();
      }
    }
  }

  /**
   * Re-render leaf labels at the current scale, coalescing rapid wheel-zoom
   * ticks into one re-render per animation frame. Density thinning keeps the
   * rendered label count small; rebuilding the subset is still cheaper to do
   * once per frame than per wheel event. Falls back to synchronous when
   * requestAnimationFrame is unavailable (e.g. some test environments).
   */
  _scheduleLabelUpdate() {
    if (!this._graph) return;
    if (typeof requestAnimationFrame !== "function") {
      this._view.replaceLabels(
        renderLeafLabels(this._graph, this.props, this._view.getScale()),
      );
      return;
    }
    if (this._labelRaf != null) {
      cancelAnimationFrame(this._labelRaf);
    }
    this._labelRaf = requestAnimationFrame(() => {
      this._labelRaf = null;
      this._view.replaceLabels(
        renderLeafLabels(this._graph, this.props, this._view.getScale()),
      );
    });
  }

  // ---- Core API -----------------------------------------------------------

  /**
   * Merge new props and re-render.  Pass `size` to also resize the viewport.
   *
   * @param {Object} next - Partial props object.
   * @returns {this}
   */
  setProps(next) {
    this.props = { ...this.props, ...next };
    if (
      next.size &&
      (next.size.width !== this._size?.width ||
        next.size.height !== this._size?.height)
    ) {
      this._view.resize(next.size.width, next.size.height);
      this._size = { width: next.size.width, height: next.size.height };
    }
    this.render();
    return this;
  }

  /**
   * Rebuild the graph and repaint all SVG layers.
   * Preserves current pan/zoom state.
   *
   * Layer z-order (back to front, per spec §6):
   *   clade fills → edges → metadata blocks → leaf-label bands →
   *   node shapes → piecharts → halos → branch labels → internal labels →
   *   leaf labels → metadata headers → clade labels
   *
   * Scalebar is rendered into the persistent overlay via setScalebar
   * (not part of mount()) so it is never cleared by re-mounts.
   *
   * @returns {this}
   */
  render() {
    // suspend() defers repaints so several setProps() calls coalesce into the
    // single render() that resume() triggers.
    if (this._suspended) return this;

    const graph = buildGraph(this.props.source, this.props);
    this._graph = graph;

    if (
      !this._warnedLargeTree &&
      graph.leaves &&
      graph.leaves.length > PERF_MAX_LEAVES
    ) {
      this._warnedLargeTree = true;
      console.warn(
        `TheiaPhyloViewer: ${graph.leaves.length} leaves exceeds the supported maximum (${PERF_MAX_LEAVES}); rendering may be slow.`,
      );
    }

    const scale = this._view.getScale();
    this._lastScale = scale;

    const halosGroup = renderHalos(
      graph,
      { ...this.props, highlightedId: this._highlightedId },
      scale,
    );

    const layers = [
      renderCladeAnnotationShapes(graph, this.props),
      renderEdges(graph, this.props),
      renderMetadataBlocks(graph, this.props),
      renderLeafLabelAnnotations(graph, this.props),
      renderNodeShapes(graph, this.props),
      renderPiecharts(graph, this.props),
      halosGroup,                                          // always present (stable z-anchor)
      renderBranchLabels(graph, this.props),
      renderInternalLabels(graph, this.props),
      renderLeafLabels(graph, this.props, scale),
      renderMetadataHeaders(graph, this.props),
      renderCladeAnnotationLabels(graph, this.props),
    ].filter(Boolean);

    this._view.mount(...layers);

    // Scalebar and branding badge into their persistent overlays (never touch
    // contentGroup, so re-mounts leave them intact).
    const viewBox = this._view.getViewBox();
    this._view.setScalebar(renderScalebar(graph, this.props, scale, viewBox));
    this._view.setLogo(renderLogo(this.props, viewBox, scale));

    const size = this.props.size || {};
    const fitKey = `${this.props.type}:${size.width}x${size.height}:${this._topoVersion}`;
    if (this._fitKey !== fitKey) {
      this._view.fit();
      this._fitKey = fitKey;
    }
    return this;
  }

  // ---- Public interaction API ---------------------------------------------

  /**
   * Normalise a node-or-id argument to a graph node, or null.
   * @param {object|string|null} nodeOrId
   * @returns {object|null}
   */
  _resolve(nodeOrId) {
    const id = nodeOrId && typeof nodeOrId === "object" ? nodeOrId.id : nodeOrId;
    return id != null && this._graph ? this._graph.nodeById[id] ?? null : null;
  }

  /**
   * Select or toggle a node (only leaf nodes enter selectedIds).
   *
   * append=false → replace the entire selection with this leaf (or clear if null/internal).
   * append=true  → toggle this leaf's membership in the current selection.
   *
   * @param {object|string|null} nodeOrId
   * @param {boolean} [append=false]
   * @returns {this}
   */

  selectNode(nodeOrId, append = false) {
    const node = this._resolve(nodeOrId);

    if (node && node.isLeaf) {
      if (append) {
        const current = new Set(this.props.selectedIds ?? []);
        if (current.has(node.id)) {
          current.delete(node.id);
        } else {
          current.add(node.id);
        }
        this.props = { ...this.props, selectedIds: Array.from(current) };
      } else {
        this.props = { ...this.props, selectedIds: [node.id] };
      }
    } else if (!node) {
      this.props = { ...this.props, selectedIds: [] };
    }

    this._view.replaceHalos(renderHalos(
      this._graph,
      { ...this.props, highlightedId: this._highlightedId },
      this._view.getScale(),
    ));
    return this;
  }

  /**
   * Select an array of leaf node ids.
   *
   * append=false → replace the entire selection.
   * append=true  → union with the current selection.
   *
   * @param {string[]} ids
   * @param {boolean} [append=false]
   * @returns {this}
   */
  selectLeafNodes(ids, append = false) {
    if (!Array.isArray(ids)) return this;
    if (append && this.props.selectedIds && this.props.selectedIds.length > 0) {
      const current = new Set(this.props.selectedIds);
      for (const id of ids) {
        current.add(id);
      }
      this.props = { ...this.props, selectedIds: Array.from(current) };
    } else if (ids.length > 0) {
      this.props = { ...this.props, selectedIds: ids };
    } else {
      this.props = { ...this.props, selectedIds: [] };
    }

    this._view.replaceHalos(renderHalos(
      this._graph,
      { ...this.props, highlightedId: this._highlightedId },
      this._view.getScale(),
    ));
    return this;
  }

  /**
   * Programmatically set the highlighted node (or clear with null).
   *
   * @param {object|string|null} [nodeOrId=null]
   * @returns {this}
   */
  highlightNode(nodeOrId = null) {
    const node = this._resolve(nodeOrId);
    this._highlightedId = (node && !node.isHidden) ? node.id : null;

    this._view.replaceHalos(renderHalos(
      this._graph,
      { ...this.props, highlightedId: this._highlightedId },
      this._view.getScale(),
    ));
    return this;
  }

  /**
   * Return the currently highlighted node object, or null.
   *
   * @returns {object|null}
   */
  getHighlightedNode() {
    if (this._highlightedId == null || !this._graph) return null;
    return this._graph.nodeById[this._highlightedId] ?? null;
  }

  // ---- Topology API -------------------------------------------------------

  /**
   * Collapse an internal node's subtree into a single marker. No-op for leaves
   * or unknown ids. Does not refit (a local edit preserves pan/zoom).
   * @param {object|string} nodeOrId
   * @returns {this}
   */
  collapseNode(nodeOrId) {
    const node = this._resolve(nodeOrId);
    if (!node || node.isLeaf) return this;
    const set = new Set(this.props.collapsedIds ?? []);
    if (!set.has(node.id)) {
      set.add(node.id);
      this.setProps({ collapsedIds: Array.from(set) });
    }
    return this;
  }

  /**
   * Expand one collapsed node, or — with no argument — expand all. Does not
   * refit.
   * @param {object|string|null} [nodeOrId=null]
   * @returns {this}
   */
  expandNode(nodeOrId = null) {
    if (nodeOrId == null) {
      if ((this.props.collapsedIds ?? []).length > 0) {
        this.setProps({ collapsedIds: [] });
      }
      return this;
    }
    const node = this._resolve(nodeOrId);
    if (!node) return this;
    const set = new Set(this.props.collapsedIds ?? []);
    if (set.delete(node.id)) {
      this.setProps({ collapsedIds: Array.from(set) });
    }
    return this;
  }

  /**
   * Display re-anchor: show only nodeOrId's subtree, or the whole tree for null.
   * Topology is unchanged; the camera resets and the view refits.
   * @param {object|string|null} [nodeOrId=null]
   * @returns {this}
   */
  setRoot(nodeOrId = null) {
    const node = nodeOrId == null ? null : this._resolve(nodeOrId);
    this._topoVersion++;
    this.setProps({
      rootId: node ? node.id : null,
      zoom: 0,
      branchZoom: 0,
      stepZoom: 0,
      centre: [0.5, 0.5],
    });
    return this;
  }

  /**
   * Structural re-root: drop a new root onto nodeOrId's branch (at its midpoint)
   * and re-hang the tree. Distance-preserving. No-op for the root or unknown
   * ids. Resets collapse state; the camera resets and the view refits.
   * @param {object|string} nodeOrId
   * @returns {this}
   */
  rerootNode(nodeOrId) {
    const node = this._resolve(nodeOrId);
    if (!node || !node.parent) return this;
    const source = rerootOnEdge(this._graph, node.id, node.branchLength / 2);
    this._topoVersion++;
    this.setProps({
      source,
      rootId: null,
      collapsedIds: [],
      zoom: 0,
      branchZoom: 0,
      stepZoom: 0,
      centre: [0.5, 0.5],
    });
    return this;
  }

  /**
   * Structural re-root at the midpoint of the tree's longest leaf-to-leaf path.
   * No-op for trees with fewer than two leaves. Distance-preserving; resets
   * collapse state; the camera resets and the view refits.
   * @returns {this}
   */
  midpointRoot() {
    if (!this._graph || this._graph.root.totalLeaves < 2) return this;
    const mid = findMidpoint(this._graph);
    if (!mid) return this;
    const source = rerootOnEdge(this._graph, mid.nodeId, mid.d);
    this._topoVersion++;
    this.setProps({
      source,
      rootId: null,
      collapsedIds: [],
      zoom: 0,
      branchZoom: 0,
      stepZoom: 0,
      centre: [0.5, 0.5],
    });
    return this;
  }

  /**
   * Ladderise: reorder every node's children so subtrees climb from small to
   * large. Pure re-ordering — distance-preserving, same leaf set. Resets
   * collapse state; the camera resets and the view refits.
   * @returns {this}
   */
  ascendingNodeOrder() {
    return this._reorderNodes("ascending");
  }

  /**
   * Ladderise the other way: subtrees descend from large to small at every node.
   * Distance-preserving; resets collapse state; the camera resets and refits.
   * @returns {this}
   */
  descendingNodeOrder() {
    return this._reorderNodes("descending");
  }

  /**
   * Shared re-hang for the two ladderise directions. No-op without a graph.
   * @param {"ascending"|"descending"} direction
   * @returns {this}
   */
  _reorderNodes(direction) {
    if (!this._graph) return this;
    const source = ladderise(this._graph, direction);
    this._topoVersion++;
    this.setProps({
      source,
      rootId: null,
      collapsedIds: [],
      zoom: 0,
      branchZoom: 0,
      stepZoom: 0,
      centre: [0.5, 0.5],
    });
    return this;
  }

  /**
   * Rotate a subtree: flip the child order at an internal node so its branches
   * swap top/bottom (or clockwise/anticlockwise in radial layouts). Toggles —
   * calling twice restores the original order. No-op for leaves or unknown ids.
   *
   * Rotation is cosmetic: topology, branch lengths and the fit box are
   * unchanged, so pan/zoom is preserved (no refit).
   *
   * Note: rotating an ancestor reorders its subtree but ids stay stable (they
   * derive from the un-rotated parse), so a flagged node keeps rotating the
   * same branch across repeated calls.
   * @param {object|string} nodeOrId
   * @returns {this}
   */
  rotateNode(nodeOrId) {
    const node = this._resolve(nodeOrId);
    if (!node || node.isLeaf) return this;
    const set = new Set(this.props.rotatedIds ?? []);
    if (!set.delete(node.id)) set.add(node.id);
    this.setProps({ rotatedIds: Array.from(set) });
    return this;
  }

  // ---- Annotation API -----------------------------------------------------

  /**
   * Create a clade annotation over the subtree rooted at `input.nodeId`. Merges
   * `annotationDefaults`, auto-generates an id when absent, fires
   * `onAnnotationCreate`, and re-renders.
   * @param {object} [input] - At least `{ nodeId }`; may carry label/style overrides.
   * @returns {object} The created annotation entry (with its id).
   */
  createAnnotation(input = {}) {
    const { list, entry } = createAnnotationEntry(this.props.annotations ?? [], input, {
      defaults: this.props.annotationDefaults,
      prefix: "annotation",
    });
    this.props.onAnnotationCreate?.(entry);
    this.setProps({ annotations: list });
    return entry;
  }

  /**
   * Patch a clade annotation by id (shallow-merge). No-op for unknown ids.
   * @param {string} id
   * @param {object} [patch]
   * @returns {object|null} The updated entry, or null if the id was unknown.
   */
  updateAnnotation(id, patch = {}) {
    const { list, entry } = updateAnnotationEntry(this.props.annotations ?? [], id, patch);
    if (!entry) return null;
    this.props.onAnnotationUpdate?.(entry);
    this.setProps({ annotations: list });
    return entry;
  }

  /**
   * Delete a clade annotation by id. No-op for unknown ids.
   * @param {string} id
   * @returns {object|null} The removed entry, or null if the id was unknown.
   */
  deleteAnnotation(id) {
    const { list, entry } = deleteAnnotationEntry(this.props.annotations ?? [], id);
    if (!entry) return null;
    this.props.onAnnotationDelete?.(entry);
    this.setProps({ annotations: list });
    return entry;
  }

  /**
   * Create a leaf-label annotation (a coloured band beyond the leaves of the
   * clade rooted at `input.nodeId`). Mirror of {@link createAnnotation} over the
   * `leafLabelAnnotations` array.
   * @param {object} [input] - At least `{ nodeId }`.
   * @returns {object} The created entry.
   */
  createLeafLabelAnnotation(input = {}) {
    const { list, entry } = createAnnotationEntry(
      this.props.leafLabelAnnotations ?? [],
      input,
      { defaults: this.props.leafLabelAnnotationDefaults, prefix: "band" },
    );
    this.props.onLeafLabelAnnotationCreate?.(entry);
    this.setProps({ leafLabelAnnotations: list });
    return entry;
  }

  /**
   * Patch a leaf-label annotation by id. No-op for unknown ids.
   * @param {string} id
   * @param {object} [patch]
   * @returns {object|null} The updated entry, or null.
   */
  updateLeafLabelAnnotation(id, patch = {}) {
    const { list, entry } = updateAnnotationEntry(
      this.props.leafLabelAnnotations ?? [],
      id,
      patch,
    );
    if (!entry) return null;
    this.props.onLeafLabelAnnotationUpdate?.(entry);
    this.setProps({ leafLabelAnnotations: list });
    return entry;
  }

  /**
   * Delete a leaf-label annotation by id. No-op for unknown ids.
   * @param {string} id
   * @returns {object|null} The removed entry, or null.
   */
  deleteLeafLabelAnnotation(id) {
    const { list, entry } = deleteAnnotationEntry(
      this.props.leafLabelAnnotations ?? [],
      id,
    );
    if (!entry) return null;
    this.props.onLeafLabelAnnotationDelete?.(entry);
    this.setProps({ leafLabelAnnotations: list });
    return entry;
  }

  // ---- Camera API ---------------------------------------------------------

  /** @returns {number} Current scale in pixels per logical unit. */
  getScale() {
    return this._view.getScale();
  }

  /**
   * Zoom so content renders at `scale` about the canvas centre.
   * @param {number} scale
   * @returns {this}
   */
  setScale(scale) {
    this._view.zoomTo(scale);
    return this;
  }

  /** @returns {number} Zoom level relative to the fitted scale (fit == 0). */
  getZoom() {
    return zoomForScale(this._view.getBaseScale(), this._view.getScale());
  }

  /**
   * Set the zoom level relative to the fitted scale (0 == fit, +1 == 2x in).
   * @param {number} zoom
   * @returns {this}
   */
  setZoom(zoom) {
    this._view.zoomTo(scaleForZoom(this._view.getBaseScale(), zoom));
    return this;
  }

  /** @returns {{x:number,y:number,w:number,h:number}} Current viewBox. */
  getView() {
    return this._view.getViewBox();
  }

  /**
   * Replace the viewBox (pan + zoom) outright. Ignores invalid input.
   * @param {{x:number,y:number,w:number,h:number}} viewBox
   * @returns {this}
   */
  setView(viewBox) {
    this._view.setViewBox(viewBox);
    return this;
  }

  /**
   * Fit the view to the rendered tree (this also resets the zoom-0 base scale).
   * @returns {this}
   */
  fitInCanvas() {
    this._view.fit();
    return this;
  }

  /**
   * Resize the canvas; the view resets and refits.
   * @param {number} width
   * @param {number} height
   * @returns {this}
   */
  resize(width, height) {
    this.setProps({ size: { width, height } });
    return this;
  }

  /** @returns {number} Independent branch-axis zoom exponent (scale ∝ 2 ** z). */
  getBranchZoom() {
    return this.props.branchZoom ?? 0;
  }

  /**
   * Set the independent branch-axis zoom exponent and re-render.
   * @param {number} zoom
   * @returns {this}
   */
  setBranchZoom(zoom) {
    this.setProps({ branchZoom: zoom });
    return this;
  }

  /** @returns {number} Independent step-axis (cross-axis) zoom exponent. */
  getStepZoom() {
    return this.props.stepZoom ?? 0;
  }

  /**
   * Set the independent step-axis zoom exponent and re-render.
   * @param {number} zoom
   * @returns {this}
   */
  setStepZoom(zoom) {
    this.setProps({ stepZoom: zoom });
    return this;
  }

  /**
   * Screen axis the branch length is drawn along, per scaleGraph's coordinate
   * mapping: branches grow along y for hierarchical layouts, x otherwise.
   * @returns {"x"|"y"}
   */
  branchZoomingAxis() {
    return this.props.type === TreeTypes.Hierarchical ? "y" : "x";
  }

  /**
   * Screen axis leaves are spaced along (the step axis) — the complement of
   * branchZoomingAxis.
   * @returns {"x"|"y"}
   */
  getStepZoomingAxis() {
    return this.props.type === TreeTypes.Hierarchical ? "x" : "y";
  }

  /** @returns {number} Smallest zoom level the width bounds allow (most out). */
  getMinZoom() {
    return zoomForScale(this._view.getBaseScale(), this.getMinScale());
  }

  /** @returns {number} Largest zoom level the width bounds allow (most in). */
  getMaxZoom() {
    return zoomForScale(this._view.getBaseScale(), this.getMaxScale());
  }

  /** @returns {number} Smallest scale the view's width bound allows. */
  getMinScale() {
    return this._view.getScaleBounds().minScale;
  }

  /** @returns {number} Largest scale the view's width bound allows. */
  getMaxScale() {
    return this._view.getScaleBounds().maxScale;
  }

  // ---- Geometry & projection API ------------------------------------------

  /**
   * Project a world (tree-space) point to client (screen-pixel) coordinates
   * using the live viewBox and the svg's on-screen rect.
   * @param {[number, number]} point - World coordinates [x, y].
   * @returns {[number, number]} Client coordinates [clientX, clientY].
   */
  projectPoint(point) {
    const rect = this._view.svg.getBoundingClientRect();
    return worldToClient(this._view.getViewBox(), rect, point);
  }

  /**
   * Unproject a client (screen-pixel) point to world (tree-space) coordinates.
   * Inverse of {@link projectPoint}.
   * @param {[number, number]} point - Client coordinates [clientX, clientY].
   * @returns {[number, number]} World coordinates [x, y].
   */
  unprojectPoint(point) {
    const rect = this._view.svg.getBoundingClientRect();
    return clientToWorld(this._view.getViewBox(), rect, point);
  }

  /**
   * Look up a node by id.
   * @param {string} id
   * @returns {object|null} The node, or null if absent / before first render.
   */
  findNodeById(id) {
    return this._resolve(id);
  }

  /** @returns {{width:number,height:number}} A copy of the canvas size. */
  getCanvasSize() {
    return { ...this.props.size };
  }

  /**
   * @returns {{min:[number,number],max:[number,number]}|null} The laid-out
   * tree's bounding box in pixel space, or null before the first render.
   */
  getWorldBounds() {
    return this._graph ? this._graph.bounds : null;
  }

  /**
   * @returns {{width:number,height:number}|null} The inner canvas box (size
   * minus padding) the tree was scaled into, or null before the first render.
   */
  getDrawingArea() {
    return this._graph ? this._graph.drawingArea : null;
  }

  /** @returns {[number,number]} The world point at the centre of the viewBox. */
  getCanvasCentrePoint() {
    const vb = this._view.getViewBox();
    return [vb.x + vb.w / 2, vb.y + vb.h / 2];
  }

  /**
   * @returns {boolean} True for orthogonal tree types (rc/hr/dg), false for
   * radial types (cr/rd).
   */
  isOrthogonal() {
    return isOrthogonalType(this.props.type);
  }

  /**
   * Pixel clearance reserved before the first metadata column begins.
   * @returns {{length:number}}
   */
  getPixelOffsets() {
    return computePixelOffsets(this._graph, this.props);
  }

  /**
   * @param {[number,number]} point - Client coordinates [clientX, clientY].
   * @returns {boolean} True when the point lies within the canvas rect.
   */
  isCanvasPointOnScreen(point) {
    const rect = this._view.svg.getBoundingClientRect();
    const [cx, cy] = point;
    return (
      cx >= rect.left && cx <= rect.right && cy >= rect.top && cy <= rect.bottom
    );
  }

  /**
   * @param {[number,number]} point - World coordinates [x, y].
   * @returns {boolean} True when the point projects onto the visible canvas.
   */
  isTreePointOnScreen(point) {
    return this.isCanvasPointOnScreen(this.projectPoint(point));
  }

  /**
   * Hit-test the DOM at a client point and return the node drawn under it.
   * elementFromPoint walks to the nearest [data-node-id] ancestor.
   * @param {[number,number]} point - Client coordinates [clientX, clientY].
   * @returns {object|null} The node under the point, or null.
   */
  pickNodeAtCanvasPoint(point) {
    if (typeof document === "undefined" || !document.elementFromPoint) {
      return null;
    }
    const [cx, cy] = point;
    const target = document.elementFromPoint(cx, cy);
    const hit = target && target.closest ? target.closest("[data-node-id]") : null;
    const id = hit ? hit.getAttribute("data-node-id") : null;
    return id != null ? this._resolve(id) : null;
  }

  // ---- Accessors & pipeline API -------------------------------------------
  //
  // Thin reflections of the current props and the built graph. Prop accessors
  // return live props values; the metadata predicates and scales delegate to
  // the pure core helpers and the built graph; the getGraph* slices re-run
  // buildGraph up to a given pass.

  /** @returns {number[]} Default leaf fill colour as [r,g,b,a]. */
  getFillColour() {
    return this.props.fillColour;
  }

  /** @returns {number[]} Default leaf stroke colour as [r,g,b,a]. */
  getStrokeColour() {
    return this.props.strokeColour;
  }

  /** @returns {number} Branch/edge stroke width in pixels. */
  getStrokeWidth() {
    return this.props.strokeWidth;
  }

  /** @returns {number} Leaf-label font size in pixels. */
  getFontSize() {
    return this.props.fontSize;
  }

  /** @returns {string} Leaf-label font family. */
  getFontFamily() {
    return this.props.fontFamily;
  }

  /** @returns {number} Node shape diameter in pixels. */
  getNodeSize() {
    return this.props.nodeSize;
  }

  /** @returns {number} Padding reserved around the drawing area in pixels. */
  getPadding() {
    return this.props.padding;
  }

  /** @returns {number} Metadata block height/width in pixels. */
  getBlockSize() {
    return this.props.blockSize;
  }

  /** @returns {number} Gap between adjacent metadata columns in pixels. */
  getBlockPadding() {
    return this.props.blockPadding;
  }

  /** @returns {number} Metadata column-header font size in pixels. */
  getBlockHeaderFontSize() {
    return this.props.blockHeaderFontSize;
  }

  /** @returns {boolean} Whether leaf node shapes are drawn. */
  getShowShapes() {
    return this.props.showShapes;
  }

  /** @returns {string} The active tree-layout type (a TreeTypes value). */
  getTreeType() {
    return this.props.type;
  }

  /** @returns {number} Width of one metadata column (blockSize + blockPadding). */
  getMetadataColumnWidth() {
    return computeMetadataColumnWidth(this.props);
  }

  /** @returns {boolean} Whether leaf labels align to a common outer edge. */
  getAlignLeafLabels() {
    return computeAlignLeafLabels(this.props);
  }

  /** @returns {boolean} Whether leaf labels are rendered. */
  hasLeafLabels() {
    return computeHasLeafLabels(this.props);
  }

  /** @returns {boolean} Whether metadata blocks are present and configured. */
  hasMetadata() {
    return computeHasMetadata(this.props);
  }

  /** @returns {boolean} Whether metadata column headers are rendered. */
  hasMetadataHeaders() {
    return computeHasMetadataHeaders(this._graph, this.props);
  }

  /** @returns {boolean} Whether metadata block labels are rendered. */
  hasMetadataLabels() {
    return computeHasMetadataLabels(this.props);
  }

  /** @returns {number|null} Pixels per branch-length unit, or null before first render. */
  getBranchScale() {
    return this._graph ? this._graph.branchScale : null;
  }

  /** @returns {number|null} Pixels per leaf step (cross axis), or null before first render. */
  getStepScale() {
    return this._graph ? this._graph.stepScale : null;
  }

  /**
   * The graph after parsing/traversal only — node hierarchy and ids, with no
   * distances, positions or styles. Re-runs the pipeline up to that pass.
   * @returns {object}
   */
  getGraphWithoutLayout() {
    return buildGraph(this.props.source, this.props, { stopAfter: "traverse" });
  }

  /**
   * The graph with root distances computed and visible leaves collected, but
   * not yet positioned.
   * @returns {object}
   */
  getGraphBeforeLayout() {
    return buildGraph(this.props.source, this.props, { stopAfter: "distances" });
  }

  /**
   * The graph positioned in pixel space (x/y, branchScale/stepScale) but
   * without leaf styles.
   * @returns {object}
   */
  getGraphAfterLayout() {
    return buildGraph(this.props.source, this.props, { stopAfter: "layout" });
  }

  /**
   * The fully built graph including leaf shapes, colours and labels — the same
   * object the viewer renders.
   * @returns {object}
   */
  getGraphWithStyles() {
    return this._graph;
  }

  // ---- Utility API --------------------------------------------------------

  /**
   * Serialise the current tree to a Newick string. Reflects live topology —
   * re-roots and ladderise re-hang the source, so the output tracks them. Leaf
   * names and internal clade labels are preserved; branch lengths are written
   * with `digits` significant decimals (trailing zeros trimmed).
   * @param {{digits?: number}} [options]
   * @returns {string}
   */
  exportNewick({ digits = 4 } = {}) {
    return serialiseNewick(this._graph, { digits });
  }

  /**
   * Serialise the whole viewer state to a JSON-safe session object: the live
   * topology (re-roots/ladderise reflected), the serialisable prop subset, the
   * current viewBox, and the highlighted node. The result survives
   * `JSON.stringify` and is the inverse of {@link importJSON}.
   *
   * Branch lengths are written at high precision (7 decimals) so a save/restore
   * cycle is effectively loss-free, unlike the human-facing {@link exportNewick}.
   *
   * @returns {{version: number, source: string, props: Object,
   *           view: {x:number,y:number,w:number,h:number}|null,
   *           highlightedId: string|null}}
   */
  exportJSON() {
    return buildSession({
      source: serialiseNewick(this._graph, { digits: 7 }),
      props: this.props,
      view: this._view.getViewBox(),
      highlightedId: this._highlightedId,
    });
  }

  /**
   * Restore a viewer from a session produced by {@link exportJSON}. Rebuilds the
   * tree from the session's Newick, re-applies the serialised props, then
   * overrides the auto-fit with the saved viewBox and re-applies the highlight.
   * Unknown shapes are ignored.
   *
   * @param {{source?: string, props?: Object,
   *          view?: {x:number,y:number,w:number,h:number}|null,
   *          highlightedId?: string|null}} session
   * @returns {this}
   */
  importJSON(session) {
    if (!session || typeof session !== "object") return this;
    const { source, props = {}, view, highlightedId } = session;
    this._topoVersion++;
    this.setProps({ source, ...props });
    if (view) this._view.setViewBox(view);
    this.highlightNode(highlightedId ?? null);
    return this;
  }

  /**
   * Serialise the current view to a standalone SVG document string.
   *
   * Behaviour: WYSIWYG — captures exactly what is mounted at the current
   * pan/zoom (the live <svg>'s viewBox), including every layer: edges, node
   * shapes, all labels, metadata blocks/headers, piecharts, clade and
   * leaf-label annotations, selection halos and the scalebar. The live DOM
   * already holds every layer, so it is serialised directly rather than
   * re-rendering from the node graph.
   *
   * The result is a self-contained document: an XML prolog, the SVG namespace
   * declaration, and explicit pixel width/height taken from the current
   * viewBox (so the file opens at a sensible size and rasterises undistorted).
   * The live view is never mutated — a deep clone is serialised.
   *
   * @returns {string} A standalone "image/svg+xml" document.
   */
  exportSVG() {
    const clone = this._view.svg.cloneNode(true);
    const vb = this._view.getViewBox();

    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    if (
      Number.isFinite(vb.w) &&
      Number.isFinite(vb.h) &&
      vb.w > 0 &&
      vb.h > 0
    ) {
      clone.setAttribute("width", String(Math.round(vb.w)));
      clone.setAttribute("height", String(Math.round(vb.h)));
    }

    const markup = new XMLSerializer().serializeToString(clone);
    return `<?xml version="1.0" encoding="UTF-8"?>\n${markup}`;
  }

  /**
   * Rasterise the current view to a PNG data URL at an optional resolution
   * multiplier.
   *
   * Pipeline (standard SVG-to-canvas technique):
   *   serialise -> Blob(image/svg+xml) -> object URL -> <img> -> drawImage onto
   *   a <canvas> sized (viewBox.w x viewBox.h) x scale -> canvas.toDataURL.
   *
   * The canvas is filled white first (an unfilled canvas is transparent, which
   * most desktop image viewers show as black or a checkerboard). The SVG holds
   * only vector primitives, system-font text, and — when the logo is enabled —
   * a single <image> whose href is a self-contained data: URL. Data URLs are
   * same-origin, so the canvas is never tainted and toDataURL cannot throw a
   * SecurityError.
   *
   * @param {number} [scale=1] - Resolution multiplier (2 = double DPI, etc.).
   * @returns {Promise<string>} Resolves to a "data:image/png;base64,…" URL.
   */
  exportPNG(scale = 1) {
    const vb = this._view.getViewBox();
    if (
      !(Number.isFinite(vb.w) && Number.isFinite(vb.h) && vb.w > 0 && vb.h > 0)
    ) {
      return Promise.reject(
        new Error("exportPNG: viewBox has no finite size (render first)"),
      );
    }

    const factor = Number.isFinite(scale) && scale > 0 ? scale : 1;
    const width = Math.max(1, Math.round(vb.w * factor));
    const height = Math.max(1, Math.round(vb.h * factor));

    const svgString = this.exportSVG();
    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("exportPNG: failed to load serialised SVG"));
      };
      img.src = url;
    });
  }

  // ---- Lifecycle & batch API ----------------------------------------------

  /**
   * Set the native hover tooltip directly. Routes through tooltipTitle so the
   * zero-width-space trick re-displays the browser tooltip even when the text is
   * unchanged; falsy text clears it.
   * @param {string|null} text
   * @returns {this}
   */
  setTooltip(text) {
    this.container.title = tooltipTitle(text, Math.floor(Math.random() * 16));
    return this;
  }

  /**
   * Suspend rendering. While suspended, setProps() still accumulates props but
   * render() is a no-op, so a batch of mutations repaints only once — on
   * resume().
   * @returns {this}
   */
  suspend() {
    this._suspended = true;
    return this;
  }

  /**
   * Resume rendering and repaint once, reflecting every prop accumulated while
   * suspended.
   * @returns {this}
   */
  resume() {
    this._suspended = false;
    this.render();
    return this;
  }

  /**
   * Replace the tree source (Newick string or pre-parsed tree) and re-render.
   * Bumps the topology version so the new tree auto-fits the viewport.
   * @param {string|object} source
   * @returns {this}
   */
  setSource(source) {
    this._topoVersion++;
    return this.setProps({ source });
  }

  /**
   * Switch the tree layout type (one of TreeTypes) and re-render.
   * @param {string} type
   * @returns {this}
   */
  setTreeType(type) {
    return this.setProps({ type });
  }

  /**
   * Inject arbitrary SVG into the persistent user overlay — a screen-fixed
   * sibling of the tree, painted above the scalebar but below the branding
   * badge. Pass null to clear it.
   * @param {SVGElement|null} group
   * @returns {this}
   */
  setOverlay(group) {
    this._view.setOverlay(group);
    return this;
  }

  /**
   * Remove all event listeners and DOM nodes added by this instance.
   */
  destroy() {
    if (this._labelRaf != null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(this._labelRaf);
      this._labelRaf = null;
    }
    this._view.destroy();
  }
}

export default TheiaPhyloViewer;
